import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, AlertTriangle, ArrowRight } from "lucide-react";
import { slugify } from "../lib/api";

const ringMap = {
  red: { dot: "dot-red", glow: "glow-red pulse-red", text: "#FF3B3B", label: "HIGH RISK" },
  yellow: { dot: "dot-yellow", glow: "glow-yellow", text: "#FFB800", label: "MODERATE" },
  green: { dot: "dot-green", glow: "glow-green", text: "#00C853", label: "SAFE" },
};

export const IngredientCard = ({ ingredient, index }) => {
  const [open, setOpen] = useState(false);
  const r = ringMap[ingredient.rating] || ringMap.yellow;

  return (
    <motion.div
      data-testid={`ingredient-card-${index}`}
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05, duration: 0.4, ease: "easeOut" }}
      whileHover={{ y: -2 }}
      className={`relative surface rounded-xl p-4 md:p-5 ${r.glow} overflow-hidden`}
    >
      <div className="flex items-start gap-4">
        <div className={`${r.dot} h-3 w-3 rounded-full mt-2 flex-shrink-0`} />
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-display text-lg font-semibold text-[color:var(--text)]">
              {ingredient.name}
            </h3>
            {ingredient.eNumber && (
              <span className="font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 rounded border border-white/10 text-[color:var(--text-2)]">
                {ingredient.eNumber}
              </span>
            )}
            {ingredient.isControversial && (
              <span className="font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 rounded bg-[#FF3B3B]/15 text-[#FF3B3B] border border-[#FF3B3B]/30 inline-flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" /> Controversial
              </span>
            )}
          </div>
          {ingredient.commonName && ingredient.commonName.toLowerCase() !== ingredient.name.toLowerCase() && (
            <div className="text-xs text-[color:var(--text-3)] mt-0.5">aka {ingredient.commonName}</div>
          )}
          <p className="text-sm text-[color:var(--text-2)] mt-2 leading-relaxed">{ingredient.whyRated}</p>
          {ingredient.dailyIntakeWarning && (
            <div className="mt-3 inline-block font-mono text-[11px] uppercase tracking-wider px-3 py-1.5 rounded border border-white/10 bg-white/[0.02]">
              <span className="text-[color:var(--text-3)]">Daily Intake —</span>{" "}
              <span style={{ color: r.text }}>{ingredient.dailyIntakeWarning}</span>
            </div>
          )}
        </div>
        <div className="flex flex-col items-end gap-2 flex-shrink-0">
          <span className="font-mono text-[10px] tracking-[0.2em] uppercase" style={{ color: r.text }}>
            {r.label}
          </span>
          <button
            data-testid={`ingredient-toggle-${index}`}
            onClick={() => setOpen(!open)}
            className="ghost-btn rounded-md px-2 py-1 text-xs inline-flex items-center gap-1"
          >
            <span>{open ? "Less" : "More"}</span>
            <ChevronDown className={`h-3 w-3 transition-transform ${open ? "rotate-180" : ""}`} />
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <div className="mt-4 pt-4 border-t border-white/10 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
              <div>
                <div className="font-mono uppercase tracking-wider text-[color:var(--text-3)] mb-1">Category</div>
                <div className="text-[color:var(--text)] capitalize">{ingredient.category || "—"}</div>
              </div>
              <div>
                <div className="font-mono uppercase tracking-wider text-[color:var(--text-3)] mb-1">E-Number</div>
                <div className="text-[color:var(--text)]">{ingredient.eNumber || "—"}</div>
              </div>
              <div>
                <div className="font-mono uppercase tracking-wider text-[color:var(--text-3)] mb-1">Rating</div>
                <div style={{ color: r.text }}>{r.label}</div>
              </div>
              <div>
                <div className="font-mono uppercase tracking-wider text-[color:var(--text-3)] mb-1">Common Name</div>
                <div className="text-[color:var(--text)]">{ingredient.commonName || ingredient.name}</div>
              </div>
            </div>
            <Link
              to={`/ingredient/${slugify(ingredient.name || "")}`}
              data-testid={`ingredient-learn-more-${index}`}
              className="mt-4 inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-wider text-[color:var(--lime)] hover:gap-2.5 transition-all"
            >
              Learn more about {ingredient.name} <ArrowRight className="h-3 w-3" />
            </Link>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default IngredientCard;
